import {
  takeLatest,
  select,
  call,
  put,
  fork,
} from 'redux-saga/effects';
import { LOADING_UPDATE_REQUEST } from '@/action-types/loading';
import { VOCABULARY_GET_REQUEST } from '@/action-types/vocabulary';
import {
  EXERCISE_START_LESSON_REQUEST,
  EXERCISE_START_LESSON_SUCCESS,
  EXERCISE_CHANGE_LESSON_REQUEST,
  EXERCISE_CHANGE_LESSON_SUCCESS,
  EXERCISE_UPDATE_PROGRESS_REQUEST,
} from '@/action-types/exercise';
import {
  LESSON_UPDATE_PROGRESS_REQUEST,
  LESSON_UPDATE_PROGRESS_SUCCESS,
} from '@/action-types/lesson';
import { ERROR_PASS_REQUEST, ERROR_UPDATE_REQUEST } from '@/action-types/error';

import api from '@/api/exercise';
import { classStart, classChange } from '@/api/class';
import { getHomeworkElements } from '@/api/training-element';
import {
  findLessonId,
  checkIsNeedHWReview,
  getHomeworkIds,
  setHomeworkElements,
  addExerciseProgress,
  addLessonProgress,
} from '@/services/book/exercise-parsers';
import { setClassStartState, setClassChangeState } from '@/services/book/exercise-states';
import { setFinishTime, setStartTime } from '@/services/load-timing';

export function* getHomeworkReview() {
  try {
    const state = yield select();
    const isNeedHWReview = yield call(checkIsNeedHWReview, state.book);

    if (isNeedHWReview) {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: true });

      const prevLessonId = yield call(findLessonId, state);
      const prevLessonExercises = yield call(api.getClassExercises, {
        lessonId: prevLessonId,
        sessionId: state.book.route.sessionId,
      });
      const hwExercises = yield call(getHomeworkIds, prevLessonExercises);

      if (hwExercises.ids.length) {
        const response = yield call(
          getHomeworkElements,
          hwExercises.ids,
          state.book.route.sessionId,
        );

        if (response.data) {
          const currentExercise = yield call(
            setHomeworkElements,
            hwExercises.aliases,
            response.data,
            state,
          );

          yield put({
            type: EXERCISE_CHANGE_LESSON_SUCCESS,
            data: { exercise: { ...state.book.exercise, currentExercise } },
          });
        }
      }
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getHomeworkReview'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: false });
  }
}

export function* classStartLessonFailure() {
  yield put({
    type: EXERCISE_CHANGE_LESSON_SUCCESS,
    data: {
      exercise: {
        currentExercise: {
          id: '0-',
        },
        exercise: [],
      },
      lesson: {},
    },
  });
  yield put({ type: ERROR_PASS_REQUEST, message: 'Failed to start class', tags: ['logic', 'classStartLessonFailure'] });
}

export function* classStartLesson(params: {
  data: {
    courseId: string;
    exerciseId: string;
    lessonId: string;
    sessionId: string;
  };
}) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'class', state: true });

    const response = yield call(classStart, params.data);

    if (response.data) {
      const receivedState = yield call(setClassStartState, response, params.data);

      yield put({
        type: EXERCISE_START_LESSON_SUCCESS,
        data: {
          ...receivedState,
          lesson: {
            ...receivedState.lesson,
            currentId: params.data.lessonId,
          },
          route: params.data,
          sync: {
            activeTab: receivedState.sync.activeTab,
          },
        },
      });
      yield put({ type: VOCABULARY_GET_REQUEST, state: receivedState });
      // @ts-ignore
      yield call(getHomeworkReview, { ...params.data });
    } else {
      yield put({ type: ERROR_PASS_REQUEST, message: 'Failed to start class', tags: ['logic', 'classStartLesson'] });
    }

    setFinishTime('open');
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'classStartLesson'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
    yield fork(classStartLessonFailure);
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'class', state: false });
  }
}

export function* classChangeLesson(params: {
  data: {
    exerciseId: string;
    lessonId: string;
  };
}) {
  try {
    setStartTime();

    const state = yield select();

    yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: true });

    const response = yield call(classChange, {
      ...params.data,
      sessionId: state.book.route.sessionId,
    });

    if (response.data) {
      const parsed = yield call(
        setClassChangeState,
        response,
        params.data,
        state.book,
      );
      yield put({
        type: EXERCISE_CHANGE_LESSON_SUCCESS,
        data: {
          ...state.book,
          ...parsed.state,
          route: {
            ...state.book.route,
            ...params.data,
          },
        },
      });
      yield put({ type: VOCABULARY_GET_REQUEST, state: parsed });
      // @ts-ignore
      yield call(getHomeworkReview, { ...params.data });
    } else {
      yield put({ type: ERROR_PASS_REQUEST, message: 'Failed to start class', tags: ['logic', 'classChangeLesson'] });
    }

    setFinishTime('process');
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'classChangeLesson'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: false });
  }
}

export function* classUpdateExercise(params: {
  exerciseId: number;
}) {
  try {
    const state = yield select();
    const {
      currentExercise,
      exercises,
    } = yield call(addExerciseProgress, state.book, params.exerciseId);

    yield put({
      type: EXERCISE_CHANGE_LESSON_SUCCESS,
      data: {
        exercise: {
          currentExercise,
          exercises,
        },
      },
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'classUpdateExercise'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
  }
}

export function* classUpdateLesson(params: { lessonId: number; }) {
  try {
    const state = yield select();
    const lessons = yield call(addLessonProgress, state.book, params.lessonId);

    yield put({
      type: LESSON_UPDATE_PROGRESS_SUCCESS,
      data: {
        ...state.book.lesson,
        lessons,
      },
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'classUpdateLesson'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
  }
}

export default function* exerciseSaga() {
  yield takeLatest<{ type: string; } & any>(EXERCISE_START_LESSON_REQUEST, classStartLesson);
  yield takeLatest<{ type: string; } & any>(EXERCISE_CHANGE_LESSON_REQUEST, classChangeLesson);
  yield takeLatest<{ type: string; } & any>(EXERCISE_UPDATE_PROGRESS_REQUEST, classUpdateExercise);
  yield takeLatest<{ type: string; } & any>(LESSON_UPDATE_PROGRESS_REQUEST, classUpdateLesson);
}
